import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './NewsletterPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/newsletters/images/${sanitized}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const NewsletterPage = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/newsletters'));
      if (!response.ok) throw new Error('Error al cargar newsletters');
      const data = await response.json();
      
      // Ordenar de más viejo a más reciente (ASC por publishedAt)
      const sorted = Array.isArray(data) ? data.sort((a, b) => {
        const dateA = new Date(a.publishedAt || a.createdAt || 0);
        const dateB = new Date(b.publishedAt || b.createdAt || 0);
        return dateA - dateB;
      }) : [];
      
      setNewsletters(sorted);
    } catch (error) {
      logError('[NewsletterPage] Error fetching newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="newsletter-page">
        <div className="newsletter-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="newsletter-page">
      {/* Hero Section */}
      <section className="newsletter-hero">
        <div className="newsletter-hero-content">
          <div className="newsletter-hero-text">
            <div className="newsletter-hero-badge">
              <span>ÚLTIMAS NOTICIAS</span>
            </div>
            <h1 className="newsletter-hero-title">
              Noticias <span className="newsletter-hero-title-accent">y Novedades</span>
            </h1>
            <p className="newsletter-hero-description">
              Mantente al día con nuestras <strong>últimas noticias y actualizaciones</strong>. 
              Descubre nuevos cursos, eventos especiales y contenido exclusivo para profesionales de la salud dental.
            </p>
            <button 
              className="newsletter-hero-button"
              onClick={() => {
                const listSection = document.querySelector('.newsletter-list-section');
                if (listSection) {
                  listSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              VER NOTICIAS
            </button>
          </div>
          <div className="newsletter-hero-illustration">
            <div className="newsletter-hero-icon">
              <i className="bi-newspaper" />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletters List Section */}
      <section className="newsletter-list-section">
        <div className="newsletter-container">
          {newsletters.length === 0 ? (
            <div className="newsletter-empty">
              <div className="newsletter-empty-icon">
                <i className="bi-envelope-paper" />
              </div>
              <h3>No hay newsletters disponibles</h3>
              <p>Próximamente publicaremos nuevas noticias y novedades.</p>
            </div>
          ) : (
            <div className="newsletter-list">
              {newsletters.map((newsletter, index) => {
                const coverUrl = resolveImageUrl(newsletter.coverImage);
                const publishedDate = formatDate(newsletter.publishedAt || newsletter.createdAt);

                return (
                  <article
                    key={newsletter.id}
                    className="newsletter-card"
                    onClick={() => navigate(`/newsletters/${newsletter.slug || newsletter.id}`)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="newsletter-card-content">
                      {coverUrl && (
                        <div className="newsletter-card-image">
                          <img src={coverUrl} alt={newsletter.title || 'Newsletter'} />
                          <div className="newsletter-card-image-overlay" />
                        </div>
                      )}
                      {!coverUrl && (
                        <div className="newsletter-card-image newsletter-card-image-placeholder">
                          <i className="bi-newspaper" />
                        </div>
                      )}
                      <div className="newsletter-card-body">
                        <div className="newsletter-card-meta">
                          <span className="newsletter-card-date">
                            <i className="bi-calendar3" />
                            {publishedDate}
                          </span>
                        </div>
                        <h2 className="newsletter-card-title">{newsletter.title || 'Sin título'}</h2>
                        {newsletter.shortDescription && (
                          <p className="newsletter-card-description">{newsletter.shortDescription}</p>
                        )}
                        <div className="newsletter-card-footer">
                          <span className="newsletter-card-link">
                            Leer más
                            <i className="bi-arrow-right" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsletterPage;
